import { db } from './firebase/config';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  onSnapshot,
  deleteDoc,
  increment
} from 'firebase/firestore';
import { getAppSettings } from '../firebase/services/appSettingsService';

const MESSAGES_COLLECTION = 'messages';

/**
 * Service de gestion du chat avec l'IA
 */
class ChatService {
  /**
   * Crée une nouvelle conversation
   * @param {string} userId - ID de l'utilisateur
   * @param {string} meetingId - ID de la réunion
   * @param {string} meetingTitle - Titre de la réunion
   * @returns {Promise<string>} - ID de la conversation créée
   */
  async createConversation(userId, meetingId, meetingTitle) {
    try {
      const conversationData = {
        userId,
        meetingId,
        meetingTitle,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        messageCount: 0,
        lastMessage: null,
        status: 'active'
      };

      const docRef = await addDoc(collection(db, MESSAGES_COLLECTION), conversationData);
      return docRef.id;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Récupère toutes les conversations d'un utilisateur pour une réunion
   * @param {string} userId - ID de l'utilisateur
   * @param {string} meetingId - ID de la réunion
   * @returns {Promise<Array>} - Liste des conversations
   */
  async getConversations(userId, meetingId) {
    try {
      const q = query(
        collection(db, MESSAGES_COLLECTION),
        where('userId', '==', userId),
        where('meetingId', '==', meetingId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const conversations = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(conv => conv.userId === userId); // Filtrage côté client pour la sécurité
      
      return conversations;
    } catch (error) {

      // En cas d'erreur de permissions, renvoyer un tableau vide
      return [];
    }
  }

  /**
   * Ajoute un message à une conversation
   * @param {string} conversationId - ID de la conversation
   * @param {Object} message - Message à ajouter
   * @returns {Promise<void>}
   */
  async addMessage(conversationId, message) {
    try {
      const conversationRef = doc(db, MESSAGES_COLLECTION, conversationId);
      const messagesRef = collection(conversationRef, 'messages');
      
      const messageData = {
        ...message,
        timestamp: serverTimestamp()
      };

      await addDoc(messagesRef, messageData);

      // Mettre à jour les métadonnées de la conversation
      const updateData = {
        lastMessage: message.content,
        updatedAt: serverTimestamp()
      };
      
      if (message.role === 'user') {
        updateData.messageCount = increment(1);
      }
      
      await updateDoc(conversationRef, updateData);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Écoute les messages d'une conversation en temps réel
   * @param {string} conversationId - ID de la conversation
   * @param {Function} callback - Fonction appelée à chaque changement
   * @returns {Function} - Fonction de désabonnement
   */
  onMessagesUpdate(conversationId, callback) {
    const conversationRef = doc(db, MESSAGES_COLLECTION, conversationId);
    const messagesRef = collection(conversationRef, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(messages);
    }, (error) => {

      // En cas d'erreur de permissions, renvoyer un tableau vide
      callback([]);
    });
  }

  /**
   * Récupère tous les messages d'une conversation
   * @param {string} conversationId - ID de la conversation
   * @returns {Promise<Array>} - Liste des messages
   */
  async getMessages(conversationId) {
    try {
      const conversationRef = doc(db, MESSAGES_COLLECTION, conversationId);
      const messagesRef = collection(conversationRef, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'asc'));

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {

      throw error;
    }
  }

  /**
   * Supprime une conversation et tous ses messages
   * @param {string} conversationId - ID de la conversation
   * @returns {Promise<void>}
   */
  async deleteConversation(conversationId) {
    try {
      // Supprimer d'abord tous les messages
      const conversationRef = doc(db, MESSAGES_COLLECTION, conversationId);
      const messagesRef = collection(conversationRef, 'messages');
      const snapshot = await getDocs(messagesRef);
      
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Puis supprimer la conversation
      await deleteDoc(conversationRef);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Envoie un message à l'API OpenAI et retourne la réponse
   * @param {Array} messages - Historique des messages
   * @param {string} transcription - Transcription de la réunion
   * @returns {Promise<string>} - Réponse de l'IA
   */
  async sendToOpenAI(messages, transcription) {
    try {
      const settings = await getAppSettings();
      const apiKey = settings?.openAISettings?.apiKey;

      if (!apiKey) {
        throw new Error('Clé API OpenAI non configurée');
      }

      // Préparer le contexte système avec la transcription
      const systemMessage = {
        role: 'system',
        content: `Tu es un assistant IA spécialisé dans l'analyse de réunions. Tu as accès à la transcription complète de la réunion ci-dessous. Réponds uniquement aux questions concernant cette réunion spécifique. Si on te demande quelque chose qui n'est pas en rapport avec la réunion, rappelle poliment que tu ne peux répondre qu'aux questions sur cette réunion.

TRANSCRIPTION DE LA RÉUNION:
${transcription}

INSTRUCTIONS:
- Sois concis et précis dans tes réponses
- Base-toi uniquement sur le contenu de la transcription
- Si une information n'est pas dans la transcription, dis-le clairement
- Utilise un ton professionnel mais amical
- Structure tes réponses avec des bullet points quand c'est pertinent`
      };

      // Construire la requête avec tout l'historique
      const apiMessages = [systemMessage, ...messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }))];

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o', // GPT-4.1 avec contexte étendu
          messages: apiMessages,
          temperature: 0.7,
          max_tokens: 2000
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Erreur lors de l\'appel à OpenAI');
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Génère le message d'accueil pour une nouvelle conversation
   * @param {string} meetingTitle - Titre de la réunion
   * @returns {string} - Message d'accueil
   */
  getWelcomeMessage(meetingTitle) {
    return `Bonjour ! 👋

Je suis votre assistant IA dédié à l'analyse de la réunion "${meetingTitle}". Je suis ici pour vous aider à mieux comprendre et exploiter le contenu de cette réunion.

Je peux répondre à vos questions concernant :
• Les points clés abordés
• Les décisions prises
• Les participants et leurs interventions
• Les actions à mener
• Tout autre élément mentionné dans la réunion

N'hésitez pas à me poser vos questions ! 😊`;
  }
}

export default new ChatService();